import { config } from '@/configs'
import { ErrorScreen } from '@/domains/appShell/error'
import { Layout, type Route } from '@/domains/appShell/layout/Layout'
import { Loading } from '@/domains/appShell/loading'
import { sessionAtom } from '@/domains/appShell/stores/auth.store'
import { SIMULATIONS_BASE } from '@/domains/performanceAnalysis/helpers/paths.helper'
import {
  ACCOUNTS_BASE,
  CONTACTS_BASE,
  IDL_TEMPLATES_BASE,
  REGISTRATIONS_BASE,
} from '@/domains/referential/helpers/paths.helper'

import { createBrowserRouter, redirect } from 'react-router-dom'

import { getDefaultStore } from 'jotai'

export const routes: Route[] = [
  // OnBoardings
  // SideMenu group (no `path`, no `guard`): accessible as soon as one of its children is. Rendered
  // as a plain item while it has a single accessible child (`collapseWhenSoleChild`).
  {
    pathname: 'onboardings',
    label: 'common.topNav.onboardings',
    collapseWhenSoleChild: true,
  },
  {
    path: '/',
    pathname: 'onboardings-list',
    childOf: 'onboardings',
    label: 'common.topNav.onboardingsList',
    // Keep the List entry (anchored at `/`) active on every onboarding page.
    // `/onboarding` covers both `/onboardings/:id` (detail) and `/onboarding/*` (sub-pages).
    activePathPrefixes: ['/onboarding'],
    lazy: () => import('@/domains/subscriptions/onboardings/pages/list/List'),
    guard: {
      permission: 'onboarding.view_onboarding',
    },
  },
  {
    path: '/onboardings/:uuid',
    pathname: 'onboarding-detail',
    lazy: () => import('@/domains/subscriptions/onboardings/pages/detail/Detail'),
    guard: {
      permission: 'onboarding.view_onboarding',
    },
  },
  {
    path: '/onboarding/aml/:uuid',
    pathname: 'onboarding-aml',
    lazy: () => import('@/domains/subscriptions/onboardings/pages/aml/Aml'),
    guard: {
      permission: 'onboarding.view_onboarding',
    },
  },
  {
    path: '/onboarding/fatca/:uuid',
    pathname: 'onboarding-fatca',
    lazy: () => import('@/domains/subscriptions/onboardings/pages/fatca/Fatca'),
    guard: {
      permission: 'onboarding.view_onboarding',
    },
  },
  {
    path: '/onboarding/aml-additional-documents/:uuid',
    pathname: 'onboarding-aml-additional-documents',
    lazy: () =>
      import('@/domains/subscriptions/onboardings/pages/amlAdditionalDocuments/AmlAdditionalDocuments'),
    guard: {
      permission: 'onboarding.view_onboarding',
    },
  },
  {
    path: '/onboarding/signatories/:uuid',
    pathname: 'onboarding-signatories',
    lazy: () => import('@/domains/subscriptions/onboardings/pages/signatories/Signatories'),
    guard: {
      permission: 'onboarding.view_onboarding',
    },
  },
  {
    path: '/onboarding/other-documents/:uuid',
    pathname: 'onboarding-other-documents',
    lazy: () => import('@/domains/subscriptions/onboardings/pages/otherDocuments/OtherDocuments'),
    guard: {
      permission: 'onboarding.view_onboarding',
    },
  },
  {
    path: '/onboarding/subscription-agreement/:uuid',
    pathname: 'onboarding-subscription-agreement',
    lazy: () =>
      import('@/domains/subscriptions/onboardings/pages/subscriptionAgreement/SubscriptionAgreement'),
    guard: {
      permission: 'onboarding.view_onboarding',
    },
  },
  {
    path: '/onboarding/histories/:uuid',
    pathname: 'onboarding-histories',
    lazy: () => import('@/domains/subscriptions/onboardings/pages/histories/Histories'),
    guard: {
      permission: 'onboarding.view_onboarding',
    },
  },
  // Legal Consultation
  {
    path: '/legal-consultations',
    pathname: 'legal-consultation',
    label: 'common.topNav.legalConsultation',
    lazy: () => import('@/domains/legalConsultations/pages/list/List'),
    guard: {
      permission: 'legal_consultation.view_legalconsultationmodel',
    },
  },
  {
    path: '/legal-consultations/:uuid',
    pathname: 'legal-consultation-detail',
    lazy: () => import('@/domains/legalConsultations/pages/detail/Detail'),
    guard: {
      permission: 'legal_consultation.view_legalconsultationmodel',
    },
  },
  {
    path: '/legal-consultations/new',
    pathname: 'legal-consultation-new',
    lazy: () => import('@/domains/legalConsultations/pages/create/Create'),
    guard: {
      permission: 'legal_consultation.view_legalconsultationmodel',
    },
  },
  {
    path: '/legal-consultations/:uuid/modify',
    pathname: 'legal-consultation-modification',
    lazy: () => import('@/domains/legalConsultations/pages/create/Modify'),
    guard: {
      permission: 'legal_consultation.view_legalconsultationmodel',
    },
  },
  {
    path: '/legal-consultations/histories/:uuid',
    pathname: 'legal-consultations-histories',
    lazy: () => import('@/domains/legalConsultations/pages/histories/Histories'),
    guard: {
      permission: 'legal_consultation.view_legalconsultationmodel',
    },
  },
  // Transfers
  // SideMenu group, same shape as Onboardings: collapsed to a plain item while List is its only
  // accessible child (every env but DEV). The List child takes the slot of the former entry.
  {
    pathname: 'transfers',
    label: 'common.topNav.transfers',
    collapseWhenSoleChild: true,
  },
  {
    path: '/transfers',
    pathname: 'transfers-list',
    childOf: 'transfers',
    label: 'common.topNav.transfersList',
    // Keep List active on the transfer detail (`/transfers/:uuid`) and sub-pages (`/transfer/*`).
    activePathPrefixes: ['/transfers', '/transfer'],
    lazy: () => import('@/domains/subscriptions/transfers/pages/list/List'),
    guard: {
      permission: 'transfer_management.view_transferoperationformmodel',
    },
  },
  {
    path: '/transfers/:uuid',
    pathname: 'transfer-detail',
    lazy: () => import('@/domains/subscriptions/transfers/pages/detail/Detail'),
    guard: {
      permission: 'transfer_management.view_transferoperationformmodel',
    },
  },
  {
    path: '/transfer/aml/:uuid',
    pathname: 'transfer-aml',
    lazy: () => import('@/domains/subscriptions/transfers/pages/aml/Aml'),
    guard: {
      permission: 'transfer_management.view_transferoperationformmodel',
    },
  },
  {
    path: '/transfer/fatca/:uuid',
    pathname: 'transfer-fatca',
    lazy: () => import('@/domains/subscriptions/transfers/pages/fatca/Fatca'),
    guard: {
      permission: 'transfer_management.view_transferoperationformmodel',
    },
  },
  {
    path: '/transfer/aml-additional-documents/:uuid',
    pathname: 'transfer-aml-additional-documents',
    lazy: () =>
      import('@/domains/subscriptions/transfers/pages/amlAdditionalDocuments/AmlAdditionalDocuments'),
    guard: {
      permission: 'transfer_management.view_transferoperationformmodel',
    },
  },
  {
    path: '/transfer/signatories/:uuid',
    pathname: 'transfer-signatories',
    lazy: () => import('@/domains/subscriptions/transfers/pages/signatories/Signatories'),
    guard: {
      permission: 'transfer_management.view_transferoperationformmodel',
    },
  },
  {
    path: '/transfer/other-documents/:uuid',
    pathname: 'transfer-other-documents',
    lazy: () => import('@/domains/subscriptions/transfers/pages/otherDocuments/OtherDocuments'),
    guard: {
      permission: 'transfer_management.view_transferoperationformmodel',
    },
  },
  {
    path: '/transfer/histories/:uuid',
    pathname: 'transfer-histories',
    lazy: () => import('@/domains/subscriptions/transfers/pages/histories/Histories'),
    guard: {
      permission: 'transfer_management.view_transferoperationformmodel',
    },
  },
  // DataRooms
  {
    path: '/datarooms',
    pathname: 'datarooms',
    label: 'common.topNav.datarooms',
    lazy: () => import('@/domains/datarooms/pages/list/List'),
    guard: { permission: 'opportunity.view_masterfund' },
  },
  {
    path: '/datarooms/:uuid',
    pathname: 'dataroom-detail',
    lazy: () => import('@/domains/datarooms/pages/detail/Detail'),
    guard: { permission: 'opportunity.view_masterfund' },
  },
  // Performance Analysis — SideMenu group (no `path`): its pages are its `childOf` entries.
  {
    pathname: 'performance-analysis',
    label: 'common.topNav.performanceAnalysis',
    guard: { permission: 'performance_analysis.forecast_export_download.view' },
  },
  {
    path: '/performance-analysis/projected-performances',
    pathname: 'projected-performances',
    childOf: 'performance-analysis',
    label: 'common.topNav.projectedPerformances',
    lazy: () => import('@/domains/performanceAnalysis/forecastExports/pages/list/List'),
    guard: { permission: 'performance_analysis.forecast_export_download.view' },
  },
  // Subscription Agreement Generation — declared after the existing entries: the guard redirects
  // to the first accessible route, so declaring it higher would change the landing page of
  // existing profiles. Top-level (no `childOf`) so that a user holding only this permission is
  // redirected to it.
  {
    path: '/subscription-agreement-generation',
    pathname: 'subscription-agreement-generation',
    label: 'common.topNav.subscriptionAgreementGeneration',
    hideNavigationWhenSoleEntry: true,
    lazy: () =>
      import('@/domains/subscriptions/subscriptionAgreementGeneration/pages/generation/Generation'),
    guard: { permission: 'onboarding_management.subscription_agreement.generator.access' },
  },
  // Video Replays — declared after Subscription Agreement Generation, the last production entry,
  // for the same reason: the guard redirects to the first accessible route, so declaring it higher
  // would change the landing page of existing profiles. Top-level so that a user holding only this
  // permission is redirected to it.
  {
    path: '/video-replays',
    pathname: 'video-replays',
    label: 'common.topNav.videoReplays',
    hideNavigationWhenSoleEntry: true,
    lazy: () => import('@/domains/videoReplays/pages/list/List'),
    guard: { permission: 'trustview_communications.video_replay.access' },
  },
  // Video replay creation form — must stay immediately after `/video-replays`: the guard redirects
  // to the first accessible top-level route, so a profile with access + create must land on the
  // list, not on the form. No `label` / `childOf`: not a navigation entry.
  {
    path: '/video-replays/new',
    pathname: 'video-replays-new',
    lazy: () => import('@/domains/videoReplays/pages/create/Create'),
    guard: { permission: 'trustview_communications.add_replayvideomodel' },
  },
  // Dedicated DataRooms — hidden in PROD
  ...(config.env === 'P'
    ? []
    : ([
        {
          path: '/dedicated-datarooms',
          pathname: 'dedicated-datarooms',
          label: 'common.topNav.dedicatedDatarooms',
          lazy: () => import('@/domains/dedicatedDatarooms/pages/list/List'),
          guard: { permission: 'specific_pages.view_dedicateddataroom' },
        },
        {
          path: '/dedicated-datarooms/:uuid',
          pathname: 'dedicated-datarooms-detail',
          lazy: () => import('@/domains/dedicatedDatarooms/pages/detail/Detail'),
          guard: { permission: 'specific_pages.view_dedicateddataroom' },
        },
        {
          path: '/dedicated-datarooms/new',
          pathname: 'dedicated-datarooms-new',
          lazy: () => import('@/domains/dedicatedDatarooms/pages/new/New'),
          guard: { permission: 'specific_pages.view_dedicateddataroom' },
        },
      ] satisfies Route[])),
  // Pages migrated from TVM (Onboardings group: Investment vehicles, Master funds, Pocket groups,
  // Pockets; Transfers group: Master funds, Pockets; Referential group: Contacts, Accounts,
  // Registrations, IDL templates; Simulations, a top-level entry) — DEV only (local + dev), UAT
  // stays aligned with PROD. Declared last: the guard redirects to the first accessible route, so
  // existing profiles keep their landing page. The static route comes before `/:uuid`.
  ...(config.env !== 'D'
    ? []
    : ([
        {
          path: '/onboardings/investment-vehicles',
          pathname: 'investment-vehicles',
          childOf: 'onboardings',
          label: 'common.topNav.investmentVehicles',
          // Wins over List's `/onboarding` prefix (most specific prefix) on this page and its detail.
          activePathPrefixes: ['/onboardings/investment-vehicles'],
          lazy: () => import('@/domains/referential/investmentVehicles/pages/list/List'),
          guard: { permission: 'referential.investment_vehicle.view' },
        },
        {
          path: '/onboardings/investment-vehicles/:uuid',
          pathname: 'investment-vehicle-detail',
          lazy: () => import('@/domains/referential/investmentVehicles/pages/detail/Detail'),
          guard: { permission: 'referential.investment_vehicle.view' },
        },
        // Master funds (`onboarding.MasterFund`): same DEV-only rule. Permission codename assumed
        // until the backend ships it.
        {
          path: '/onboardings/master-funds',
          pathname: 'master-funds',
          childOf: 'onboardings',
          label: 'common.topNav.masterFunds',
          activePathPrefixes: ['/onboardings/master-funds'],
          lazy: () => import('@/domains/referential/masterFunds/pages/list/List'),
          guard: { permission: 'referential.master_fund.view' },
        },
        {
          path: '/onboardings/master-funds/:uuid',
          pathname: 'master-funds-detail',
          lazy: () => import('@/domains/referential/masterFunds/pages/detail/Detail'),
          guard: { permission: 'referential.master_fund.view' },
        },
        // Pocket groups (`onboarding.PocketGroup`): same DEV-only rule. Permission codename assumed
        // until the backend ships it.
        {
          path: '/onboardings/pocket-groups',
          pathname: 'pocket-groups',
          childOf: 'onboardings',
          label: 'common.topNav.pocketGroups',
          activePathPrefixes: ['/onboardings/pocket-groups'],
          lazy: () => import('@/domains/referential/pocketGroups/pages/list/List'),
          guard: { permission: 'referential.pocket_group.view' },
        },
        {
          path: '/onboardings/pocket-groups/:uuid',
          pathname: 'pocket-groups-detail',
          lazy: () => import('@/domains/referential/pocketGroups/pages/detail/Detail'),
          guard: { permission: 'referential.pocket_group.view' },
        },
        // Pockets (`onboarding.Pocket`): same DEV-only rule. Permission codename assumed until the
        // backend ships it.
        {
          path: '/onboardings/pockets',
          pathname: 'pockets',
          childOf: 'onboardings',
          label: 'common.topNav.pockets',
          activePathPrefixes: ['/onboardings/pockets'],
          lazy: () => import('@/domains/referential/pockets/pages/list/List'),
          guard: { permission: 'referential.pocket.view' },
        },
        {
          path: '/onboardings/pockets/:uuid',
          pathname: 'pockets-detail',
          lazy: () => import('@/domains/referential/pockets/pages/detail/Detail'),
          guard: { permission: 'referential.pocket.view' },
        },
        // Transfers group: the same Master funds pages, mounted under `/transfers/master-funds`
        // (same permission as in Onboardings). The pages read their links from
        // `useReferentialPaths`, so they stay in this group. Static paths win over `/transfers/:uuid`.
        {
          path: '/transfers/master-funds',
          pathname: 'transfers-master-funds',
          childOf: 'transfers',
          label: 'common.topNav.masterFunds',
          activePathPrefixes: ['/transfers/master-funds'],
          lazy: () => import('@/domains/referential/masterFunds/pages/list/List'),
          guard: { permission: 'referential.master_fund.view' },
        },
        {
          path: '/transfers/master-funds/:uuid',
          pathname: 'transfers-master-funds-detail',
          lazy: () => import('@/domains/referential/masterFunds/pages/detail/Detail'),
          guard: { permission: 'referential.master_fund.view' },
        },
        // Same for Pockets, under `/transfers/pockets`.
        {
          path: '/transfers/pockets',
          pathname: 'transfers-pockets',
          childOf: 'transfers',
          label: 'common.topNav.pockets',
          activePathPrefixes: ['/transfers/pockets'],
          lazy: () => import('@/domains/referential/pockets/pages/list/List'),
          guard: { permission: 'referential.pocket.view' },
        },
        {
          path: '/transfers/pockets/:uuid',
          pathname: 'transfers-pockets-detail',
          lazy: () => import('@/domains/referential/pockets/pages/detail/Detail'),
          guard: { permission: 'referential.pocket.view' },
        },
        // Referential group (Contacts, soon Accounts, IDL templates…): the base data of the platform,
        // an accordion like Onboardings. The prefix comes from `CONTACTS_BASE` (built on
        // `REFERENTIAL_BASE`), never written out here.
        {
          pathname: 'referential',
          label: 'common.topNav.referential',
        },
        // Contacts (`core.Contact`). Permission codename assumed until the backend ships it (TVM:
        // `core.view_contact`).
        {
          path: CONTACTS_BASE,
          pathname: 'contacts',
          childOf: 'referential',
          label: 'common.topNav.contacts',
          activePathPrefixes: [CONTACTS_BASE],
          lazy: () => import('@/domains/referential/contacts/pages/list/List'),
          guard: { permission: 'referential.contact.view' },
        },
        {
          path: `${CONTACTS_BASE}/:uuid`,
          pathname: 'contacts-detail',
          lazy: () => import('@/domains/referential/contacts/pages/detail/Detail'),
          guard: { permission: 'referential.contact.view' },
        },
        // Accounts (`core.Account`): same group. TVM parity: the Django permission TVM checks
        // (`core.view_account`), which already exists.
        {
          path: ACCOUNTS_BASE,
          pathname: 'accounts',
          childOf: 'referential',
          label: 'common.topNav.accounts',
          activePathPrefixes: [ACCOUNTS_BASE],
          lazy: () => import('@/domains/referential/accounts/pages/list/List'),
          guard: { permission: 'core.view_account' },
        },
        {
          path: `${ACCOUNTS_BASE}/:uuid`,
          pathname: 'accounts-detail',
          lazy: () => import('@/domains/referential/accounts/pages/detail/Detail'),
          guard: { permission: 'core.view_account' },
        },
        // Registrations (`registration.RegistrationInformation`): same group, one row per
        // invitation. The permission codename is a hypothesis (#4270): the backend has not shipped
        // a `referential.registration.*` yet, TVM checks `registration.view_registrationinformation`.
        // If it names it otherwise, this guard redirects and the page stays out of reach.
        {
          path: REGISTRATIONS_BASE,
          pathname: 'registrations',
          childOf: 'referential',
          label: 'common.topNav.registrations',
          activePathPrefixes: [REGISTRATIONS_BASE],
          lazy: () => import('@/domains/referential/registrations/pages/list/List'),
          guard: { permission: 'referential.registration.view' },
        },
        {
          path: `${REGISTRATIONS_BASE}/:uuid`,
          pathname: 'registrations-detail',
          lazy: () => import('@/domains/referential/registrations/pages/detail/Detail'),
          guard: { permission: 'referential.registration.view' },
        },
        // IDL templates (`core.IDLTemplate`): same group, one row per template. The permission
        // codename is a hypothesis (#4270), TVM checks `core.view_idltemplate`.
        {
          path: IDL_TEMPLATES_BASE,
          pathname: 'idl-templates',
          childOf: 'referential',
          label: 'common.topNav.idlTemplates',
          activePathPrefixes: [IDL_TEMPLATES_BASE],
          lazy: () => import('@/domains/referential/idlTemplates/pages/list/List'),
          guard: { permission: 'referential.idl_template.view' },
        },
        {
          path: `${IDL_TEMPLATES_BASE}/:uuid`,
          pathname: 'idl-templates-detail',
          lazy: () => import('@/domains/referential/idlTemplates/pages/detail/Detail'),
          guard: { permission: 'referential.idl_template.view' },
        },
        // Simulations (`performance_analysis.SimulationModel`): a top-level entry, no group (like the
        // mockup; the "Performance Analysis" group is guarded by the forecast export permission,
        // which would hide Simulations from profiles without it). The permission is the one TVM and
        // the existing `GET bo/v1/simulations` check (`CanViewSimulations`).
        {
          path: SIMULATIONS_BASE,
          pathname: 'simulations',
          label: 'common.topNav.simulations',
          activePathPrefixes: [SIMULATIONS_BASE],
          lazy: () => import('@/domains/performanceAnalysis/simulations/pages/list/List'),
          guard: { permission: 'performance_analysis.view_simulationmodel' },
        },
        {
          path: `${SIMULATIONS_BASE}/:uuid`,
          pathname: 'simulations-detail',
          lazy: () => import('@/domains/performanceAnalysis/simulations/pages/detail/Detail'),
          guard: { permission: 'performance_analysis.view_simulationmodel' },
        },
      ] satisfies Route[])),
]

export const router = createBrowserRouter([
  {
    path: '/',
    Component: () => <Layout routes={routes} />,
    hydrateFallbackElement: <Loading />,
    children: routes,
  },
  // POC: outside `Layout` (no permission guard, no app chrome) — opened in a new tab by
  // `useOpenAuthorizedFileInNewTab`. The loader still requires an active session (redirects
  // home otherwise) since this route isn't reachable through `Main`'s login gate on its own.
  {
    path: '/file-viewer',
    loader: () => (getDefaultStore().get(sessionAtom) ? null : redirect('/')),
    lazy: () => import('@/domains/appShell/fileViewer/FileViewerRouter'),
  },
  {
    path: '*',
    Component: ErrorScreen,
  },
])
